import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Box,
  Divider,
  Group,
  Indicator,
  Popover,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import {
  IconBell,
  IconEye,
  IconLinkOff,
  IconPackage,
  IconStar,
  IconListCheck,
  IconTrash,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotificationCenter } from '@/hooks/useNotificationCenter';
import type { AppNotification, AppNotificationType } from '@/Api';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { useStore } from '@/store/Store';

function typeIcon(type: AppNotificationType) {
  switch (type) {
    case 'product_created':
      return IconPackage;
    case 'listing_approved':
      return IconListCheck;
    case 'share_expired':
      return IconLinkOff;
    case 'star_milestone':
      return IconStar;
    case 'views_trending':
      return IconEye;
    case 'product_trashed':
      return IconTrash;
    case 'product_trash_expiring':
      return IconAlertTriangle;
    default:
      return IconBell;
  }
}

function formatRelativeTime(
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return t('notificationCenter.justNow');
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return t('notificationCenter.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 48) return t('notificationCenter.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notificationCenter.daysAgo', { count: days });
}

function NotificationRow({
  item,
  accent,
  onOpen,
}: {
  item: AppNotification;
  accent: string;
  onOpen: (item: AppNotification) => void;
}) {
  const { t } = useTranslation();
  const unread = !item.readAt;
  const Icon = typeIcon(item.type);
  const title = t(`notificationCenter.types.${item.type}.title`, {
    ...item.payload,
    defaultValue: item.type,
  });
  const body = t(`notificationCenter.types.${item.type}.body`, {
    ...item.payload,
    defaultValue: '',
  });

  return (
    <UnstyledButton
      onClick={() => onOpen(item)}
      style={{
        display: 'block',
        width: '100%',
        padding: '10px 12px',
        borderRadius: 8,
        backgroundColor: unread ? colorWithAlpha(accent, 0.08) : 'transparent',
        textAlign: 'left',
      }}
    >
      <Group wrap="nowrap" align="flex-start" gap="sm">
        <ThemeIcon
          size={32}
          radius="md"
          variant="light"
          color={unread ? 'red' : 'gray'}
          style={unread ? { backgroundColor: colorWithAlpha(accent, 0.15), color: accent } : undefined}
        >
          <Icon size={16} />
        </ThemeIcon>
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" gap={6} wrap="nowrap">
            <Text size="sm" fw={unread ? 700 : 600} lineClamp={1}>
              {title}
            </Text>
            {unread ? (
              <Box
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: '#e11d48',
                  flexShrink: 0,
                }}
              />
            ) : null}
          </Group>
          <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
            {body}
          </Text>
          <Text size="xs" c="dimmed" mt={4}>
            {formatRelativeTime(item.createdAt, t)}
          </Text>
        </Box>
      </Group>
    </UnstyledButton>
  );
}

export function NotificationBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accent = useStore((s) => s.userProfile.qrCodeColor) || '#e11d48';
  const [opened, setOpened] = useState(false);
  const {
    isLoggedIn,
    items,
    unreadCount,
    loading,
    openAndRefresh,
    markRead,
  } = useNotificationCenter();

  const badgeLabel = useMemo(() => {
    if (unreadCount <= 0) return undefined;
    return unreadCount > 99 ? '99+' : String(unreadCount);
  }, [unreadCount]);

  if (!isLoggedIn) return null;

  const handleOpenChange = (next: boolean) => {
    setOpened(next);
    if (next) void openAndRefresh();
  };

  const handleItemOpen = async (item: AppNotification) => {
    if (!item.readAt) await markRead(item._id);
    setOpened(false);
    if (item.href) navigate(item.href);
  };

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      position="bottom-end"
      width={340}
      shadow="md"
      radius="md"
      withinPortal
    >
      <Popover.Target>
        {/* Badge lives inside the button so clicks on the red count still open the popover */}
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label={
            badgeLabel
              ? `${t('notificationCenter.title')} (${t('notificationCenter.unread', { count: unreadCount })})`
              : t('notificationCenter.title')
          }
          onClick={() => handleOpenChange(!opened)}
        >
          <Indicator
            label={badgeLabel}
            size={16}
            offset={2}
            color="red"
            disabled={!badgeLabel}
            processing={unreadCount > 0}
            styles={{ indicator: { pointerEvents: 'none' } }}
          >
            <IconBell size={20} stroke={1.75} style={{ display: 'block' }} />
          </Indicator>
        </ActionIcon>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <Group justify="space-between" px="sm" py="xs">
          <Text fw={700} size="sm">
            {t('notificationCenter.title')}
          </Text>
        </Group>
        <Divider />
        <ScrollArea.Autosize mah={360} type="scroll">
          {loading && items.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="xl">
              {t('notificationCenter.loading')}
            </Text>
          ) : items.length === 0 ? (
            <Stack align="center" gap={6} py="xl" px="md">
              <IconBell size={28} stroke={1.25} opacity={0.35} />
              <Text size="sm" c="dimmed" ta="center">
                {t('notificationCenter.empty')}
              </Text>
            </Stack>
          ) : (
            <Stack gap={2} p={6}>
              {items.map((item) => (
                <NotificationRow
                  key={item._id}
                  item={item}
                  accent={accent}
                  onOpen={handleItemOpen}
                />
              ))}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Popover.Dropdown>
    </Popover>
  );
}
